import React, { useState } from 'react';
import { FilePlus, Clock, TrendingUp, Calendar, BookOpen, Scissors, ArrowRight, Pencil, Eye } from 'lucide-react';
import { Quote, User } from '../types';
import { formatCurrency } from '../utils/helpers';
import QuoteQuickLook from '../components/QuoteQuickLook';

interface DashboardProps {
  user: User;
  quotes: Quote[];
  onNavigate: (page: any) => void;
  onEditQuote?: (quote: Quote) => void;
  onUpdateStatus?: (id: string, status: string) => void;
  onConvertToInvoice?: (id: string, invoiceNumber: string) => void;
  onMarkAdvanceReceived?: (id: string) => void;
}

export default function Dashboard({ user, quotes, onNavigate, onEditQuote, onUpdateStatus, onConvertToInvoice, onMarkAdvanceReceived }: DashboardProps) {
  // ID-based quickLook so status changes reflect immediately without closing/reopening
  const [quickLookId, setQuickLookId] = useState<string | null>(null);
  const quickLook = quickLookId ? quotes.find(q => q.id === quickLookId) || null : null;
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const recentQuotes = quotes.slice(0, 5);
  const thisMonth = quotes.filter(q => {
    const d = new Date(q.createdAt);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const confirmedTotal = thisMonth.filter(q => q.status === 'confirmed').reduce((sum, q) => sum + q.quotedAmount, 0);

  const STATUS_COLORS: Record<string, string> = {
    draft: 'badge-amber', sent: 'badge-teal', confirmed: 'badge-best', cancelled: 'badge-red'
  };

  return (
    <div style={{ padding: 28, maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Good {getGreeting()}, {user.name.split(' ')[0]} 👋</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>{today}</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Total Quotes', value: quotes.length, icon: <Clock size={18} />, color: 'var(--blue)' },
          { label: 'This Month', value: thisMonth.length, icon: <TrendingUp size={18} />, color: 'var(--teal)' },
          { label: 'Confirmed', value: thisMonth.filter(q => q.status === 'confirmed').length, icon: <TrendingUp size={18} />, color: 'var(--teal-light)' },
          { label: 'Confirmed Value', value: formatCurrency(confirmedTotal), icon: <TrendingUp size={18} />, color: 'var(--gold)' },
        ].map((stat, i) => (
          <div key={i} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{stat.label}</p>
                <p style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>{stat.value}</p>
              </div>
              <div style={{ color: stat.color, opacity: 0.6 }}>{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
        {/* Recent Quotes */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Quotes</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('quote-history')}>
              View all <ArrowRight size={12} />
            </button>
          </div>

          {recentQuotes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
              <Clock size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>No quotes yet.</p>
              <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => onNavigate('new-quote')}>
                Create first quote
              </button>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Quote #</th>
                    <th>Customer</th>
                    <th>Event</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recentQuotes.map(q => (
                    <tr key={q.id} style={{ cursor: 'pointer' }} onClick={() => setQuickLookId(q.id)}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{q.quoteNumber}</td>
                      <td style={{ fontWeight: 500 }}>{q.customerName}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{q.eventName}</td>
                      <td style={{ color: 'var(--gold)', fontWeight: 600 }}>{formatCurrency(q.quotedAmount)}</td>
                      <td><span className={`badge ${STATUS_COLORS[q.status] || 'badge-amber'}`}>{q.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={(e) => { e.stopPropagation(); setQuickLookId(q.id); }} title="Quick look">
                            <Eye size={13} />
                          </button>
                          {onEditQuote && !(q as any).isGeneralPlanner && (
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={(e) => { e.stopPropagation(); onEditQuote(q); }} title="Edit">
                              <Pencil size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card">
            <h3 style={{ marginBottom: 14 }}>Quick Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'New Quote', sub: 'Create a quote for a customer', icon: <FilePlus size={16} />, page: 'new-quote', color: 'var(--gold)' },
                { label: 'Walk-in Album', sub: 'Album only quote', icon: <BookOpen size={16} />, page: 'walk-in-album', color: 'var(--teal-light)' },
                { label: 'Walk-in Post Prod.', sub: 'Editing only quote', icon: <Scissors size={16} />, page: 'walk-in-pp', color: 'var(--blue-light)' },
                { label: 'Booking Calendar', sub: 'Check availability', icon: <Calendar size={16} />, page: 'calendar', color: 'var(--text-secondary)' },
              ].map((action, i) => (
                <button key={i} onClick={() => onNavigate(action.page)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-hover)', border: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', width: '100%' }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-light)'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'}
                >
                  <span style={{ color: action.color }}>{action.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{action.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{action.sub}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {quickLook && (
        <QuoteQuickLook
          quote={quickLook}
          onClose={() => setQuickLookId(null)}
          onEdit={onEditQuote}
          onStatusChange={onUpdateStatus}
          onConvertToInvoice={onConvertToInvoice}
          onMarkAdvanceReceived={onMarkAdvanceReceived}
        />
      )}
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
